import { useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useDropzone } from 'react-dropzone'
import toast from 'react-hot-toast'
import { ButtonLegacy, type ButtonProps } from '@/components/ui/button-legacy'
import { Dropzone } from '@/components/ui/dropzone'
import { PlusIcon } from '@/components/ui/icons'
import { Spinner } from '@/components/ui/spinner'
import { MAXIMUM_IMAGES_ALLOWED } from '@/features/experience-shell/components/photo-step-gallery'
import type { Experience } from '@/features/experience-shell/types'
import { httpExperienceServiceConfirmUploadPhoto } from '@/features/experience-vetting/http/experience-vetting.http.ts'
import { useBulkPresignedExperienceVettingPhotos } from '@/features/experience-vetting/queries/use-bulk-presigned-experience-vetting-photos.ts'
import { VETTING_MAX_FILE_TO_UPLOAD } from '@/features/ops-vetting/costants.ts'
import {
	type GalleryItem,
	useExperienceVettingPhotoUpload
} from '@/features/service-vetting/store/services-photo-upload.ts'
import { useUploadPhotos } from '@/hooks/use-upload-photos'
import { QUERY_KEYS } from '@/query-client'
import type { TKey } from '@/translations/i18n'
import { useTranslation } from '@/translations/i18n'
import type { FileToUpload } from '@/types/commons.ts'

export const ExperienceVettingCriteriaUploadButton = ({
	experienceId,
	galleryId,
	disabled,
	buttonText = 'vetting:criteria.service.gallery.upload_photos',
	buttonProps,
	withIcon = true,
	galleryCounter = 0
}: {
	experienceId: number
	galleryId: number
	disabled?: boolean
	buttonText?: TKey
	buttonProps?: ButtonProps
	withIcon?: boolean
	galleryCounter?: number
}) => {
	const { t } = useTranslation()
	const [acceptedFiles, setAcceptedFiles] = useState<File[]>([])
	const setUploadingItemsStatus = useExperienceVettingPhotoUpload(
		(store) => store.setUploadingItemsStatus
	)
	const addUploadProgressValues = useExperienceVettingPhotoUpload(
		(store) => store.addUploadProgressValues
	)
	const deleteGalleryItem = useExperienceVettingPhotoUpload(
		(store) => store.deleteGalleryItem
	)
	const addGalleryItems = useExperienceVettingPhotoUpload(
		(store) => store.addGalleryItems
	)
	const queryClient = useQueryClient()
	const bulkPresignedQuery = useBulkPresignedExperienceVettingPhotos({
		urlParams: { experienceId, galleryId },
		options: {
			onSuccess: (data) => {
				const galleryItems: GalleryItem[] = data.map((item, index) => ({
					id: item.media.id,
					url: URL.createObjectURL(acceptedFiles[index]),
					status: 'pending',
					name: item.media.name,
					thumbnails: null //TODO: add thumbnails
				}))
				const uploadingItems: FileToUpload[] = data.map(
					(item, index) => ({
						id: item.media.id,
						presignedUrl: item.presignedUrl,
						file: acceptedFiles[index]
					})
				)
				addGalleryItems(galleryItems)
				uploadPhotos(uploadingItems)
			}
		}
	})

	const uploadPhotos = useUploadPhotos({
		storeAction: { setUploadingItemsStatus, addUploadProgressValues },
		onConfirm: async (imageId) => {
			const response = await httpExperienceServiceConfirmUploadPhoto({
				urlParams: { experienceId, galleryId, mediaId: imageId },
				params: { responseType: 'extended' }
			})
			queryClient.invalidateQueries({
				queryKey: [QUERY_KEYS.EXPERIENCE_VETTING_GALLERY_IMAGES]
			})
			const queryKey = [QUERY_KEYS.EXPERIENCE_VETTING, experienceId]
			await queryClient.cancelQueries({ queryKey })
			const previousData = queryClient.getQueryData<Experience>(queryKey)
			queryClient.setQueriesData<Experience>(
				{ queryKey },
				previousData
					? {
							...previousData,
							gallery: {
								...previousData.gallery,
								images: [
									...previousData.gallery.images,
									response
								].sort((a, b) => {
									if (a.position === null) {
										return 1
									}
									if (b.position === null) {
										return -1
									}
									return a.position - b.position
								})
							}
						}
					: previousData
			)
			deleteGalleryItem(imageId)
		}
	})
	const maxFilesAllowed = MAXIMUM_IMAGES_ALLOWED - galleryCounter
	const dropzoneState = useDropzone({
		accept: {
			'image/jpeg': ['.jpeg'],
			'image/jpg': ['.jpg'],
			'image/png': ['.png']
		},
		maxFiles: VETTING_MAX_FILE_TO_UPLOAD,
		multiple: true,
		onDropAccepted: (acceptedFiles) => {
			const filesToUpload = acceptedFiles.slice(0, maxFilesAllowed)
			if (filesToUpload.length > 0) {
				setAcceptedFiles(filesToUpload)
				bulkPresignedQuery.mutate({
					medias: filesToUpload.map((file) => file.name)
				})
			}
		},
		onDropRejected: (rejectedFiles) => {
			if (rejectedFiles.length > VETTING_MAX_FILE_TO_UPLOAD) {
				return toast.error(
					t('errors:dropzone_max_file', {
						count: VETTING_MAX_FILE_TO_UPLOAD
					})
				)
			}
			console.error('Rejected files: ', rejectedFiles)
		}
	})

	return (
		<Dropzone state={dropzoneState}>
			<ButtonLegacy
				disabled={
					bulkPresignedQuery.isPending ||
					disabled ||
					galleryCounter >= MAXIMUM_IMAGES_ALLOWED
				}
				type='button'
				variant={'secondary'}
				{...buttonProps}
			>
				{bulkPresignedQuery.isPending ? (
					<Spinner size={17} />
				) : (
					withIcon && <PlusIcon />
				)}
				{t(buttonText)}
			</ButtonLegacy>
		</Dropzone>
	)
}
